// ============================================
// COURRIER ÉCARTÉ — ÉCRAN
// ============================================
// E-mails reçus qui n'ont pas ouvert de dossier : notifications automatiques
// (règles) et messages que l'analyse IA juge sans rapport avec le SAV. Rien
// n'est parti vers leurs expéditeurs. Si l'un d'eux est une vraie demande,
// « Créer un dossier quand même » ouvre un dossier à qualifier (après
// confirmation) et donne le lien vers ce dossier.
//
// Ordinateur : tableau. Sous 1024 px : cartes, boutons d'au moins 40 px.
// Accessible depuis le menu (sous « Dossiers ») et depuis « Boîtes mail ».

import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2, FilePlus2, Inbox, Loader2, MailX, RefreshCw, XCircle } from 'lucide-react';
import { Badge, Button, Card, Modal, Select } from '../../shared/ui';
import { useEcranEtroit } from '../../shared/responsive';
import { dateLisible } from '../connecteurs/diagnostic';
import {
  MOTIFS_FILTRE, chargerCourrierEcarte, classeMotif, confianceLisible, creerDossierDepuisCourrier, expediteurLisible,
  motifLisible, signalerCourrierModifie, texteConfirmationCreation,
  type CourrierEcarte, type DossierCreeDepuisCourrier, type PageCourrierEcarte,
} from './courrier';

/** Cibles tactiles d'au moins 40 px sous 1024 px ; rendu d'origine au-delà. */
const TACTILE = 'min-h-[40px] lg:min-h-0';
const LIEN = 'inline-flex items-center gap-1 text-sm font-semibold text-sapin-700 hover:text-sapin-900 hover:underline';
const ACTIONS_FENETRE = 'flex flex-col gap-2 sm:flex-row sm:justify-end';
const BOUTON_FENETRE = `w-full sm:w-auto ${TACTILE}`;

/** Motif, confiance, résumé et raison : même bloc dans le tableau et les cartes. */
const DetailMotif: React.FC<{ c: CourrierEcarte }> = ({ c }) => {
  const confiance = confianceLisible(c.confiance);
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={classeMotif(c.motif)}>{motifLisible(c)}</Badge>
        {confiance && <span className="text-xs font-medium text-graphite tnum">{confiance}</span>}
      </div>
      {c.resume && <p className="mt-1 text-sm text-encre break-words">{c.resume}</p>}
      {c.raison && <p className="mt-0.5 text-xs text-graphite break-words">Raison : {c.raison}</p>}
      {c.apercu && (
        <details className="mt-1 text-xs text-graphite">
          <summary className="cursor-pointer font-semibold text-sapin-700 hover:text-sapin-900 py-2.5 lg:py-0.5">Début du message</summary>
          <p className="mt-1 whitespace-pre-wrap break-words">{c.apercu}</p>
        </details>
      )}
    </div>
  );
};

/** Lien vers le dossier créé, ou bouton « Créer un dossier quand même ». */
const ActionCourrier: React.FC<{
  c: CourrierEcarte;
  cree?: DossierCreeDepuisCourrier;
  onCreer: (c: CourrierEcarte) => void;
  onOuvrirDossier?: (id: string) => void;
  pleineLargeur?: boolean;
}> = ({ c, cree, onCreer, onOuvrirDossier, pleineLargeur }) => {
  const ticketId = cree?.ticketId ?? c.ticketId;
  const ticketRef = cree?.ticketRef ?? c.ticketRef;
  if (ticketId) {
    const libelle = `Dossier ${ticketRef ?? ''}`.trim();
    return onOuvrirDossier ? (
      <button type="button" onClick={() => onOuvrirDossier(ticketId)} className={`${LIEN} ${TACTILE}`}>
        {libelle}
      </button>
    ) : <span className="text-sm text-graphite">{libelle}</span>;
  }
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => onCreer(c)}
      className={`${TACTILE} ${pleineLargeur ? 'w-full' : 'whitespace-nowrap'}`}
    >
      <FilePlus2 className="w-4 h-4 mr-1.5" aria-hidden="true" />
      Créer un dossier quand même
    </Button>
  );
};

const CourrierEcarteView: React.FC<{
  onOuvrirDossier?: (id: string) => void;
  /** Retour à l'écran « Boîtes mail » (lien en tête d'écran). */
  onBoitesMail?: () => void;
}> = ({ onOuvrirDossier, onBoitesMail }) => {
  const etroit = useEcranEtroit();
  const [motif, setMotif] = useState('');
  const [page, setPage] = useState(1);
  const [donnees, setDonnees] = useState<PageCourrierEcarte | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const [aConfirmer, setAConfirmer] = useState<CourrierEcarte | null>(null);
  const [creation, setCreation] = useState(false);
  const [erreurCreation, setErreurCreation] = useState<string | null>(null);
  const [crees, setCrees] = useState<Record<string, DossierCreeDepuisCourrier>>({});
  const [dernierCree, setDernierCree] = useState<DossierCreeDepuisCourrier | null>(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      setDonnees(await chargerCourrierEcarte({ motif, page }));
    } catch (e) {
      setErreur(e instanceof Error && e.message ? e.message : 'Chargement impossible');
    } finally {
      setChargement(false);
    }
  }, [motif, page]);

  useEffect(() => { void charger(); }, [charger]);

  const changerMotif = (v: string) => { setMotif(v); setPage(1); };

  const demanderCreation = (c: CourrierEcarte) => {
    setErreurCreation(null);
    setAConfirmer(c);
  };

  const confirmerCreation = async () => {
    if (!aConfirmer || creation) return;
    setCreation(true);
    setErreurCreation(null);
    try {
      const r = await creerDossierDepuisCourrier(aConfirmer.id);
      setCrees(x => ({ ...x, [aConfirmer.id]: r }));
      setDernierCree(r);
      setAConfirmer(null);
      signalerCourrierModifie();
    } catch (e) {
      setErreurCreation(e instanceof Error && e.message ? e.message : 'Le dossier n’a pas pu être créé.');
    } finally {
      setCreation(false);
    }
  };

  const elements = donnees?.elements ?? [];
  const total = donnees?.total ?? 0;
  const pages = donnees?.pages ?? 1;

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {onBoitesMail && (
            <button type="button" onClick={onBoitesMail} className={`${LIEN} mb-1 ${TACTILE}`}>
              <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Boîtes mail
            </button>
          )}
          <h1 className="text-2xl font-semibold text-encre">Courrier écarté</h1>
          <p className="text-sm text-graphite mt-1 max-w-3xl">
            E-mails reçus qui n’ont pas ouvert de dossier : notifications automatiques et messages que
            l’analyse IA juge sans rapport avec le SAV. Rien n’a été envoyé à leurs expéditeurs.
            Si l’un d’eux est une vraie demande, créez le dossier.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2 w-full sm:w-auto">
          <label className="block grow sm:grow-0 sm:w-60">
            <span className="block text-xs font-semibold text-graphite mb-1">Motif</span>
            <Select value={motif} options={MOTIFS_FILTRE} onChange={e => changerMotif(e.target.value)} />
          </label>
          <Button variant="secondary" onClick={() => void charger()} disabled={chargement} className={`grow sm:grow-0 ${TACTILE}`}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${chargement ? 'animate-spin' : ''}`} aria-hidden="true" /> Actualiser
          </Button>
        </div>
      </header>

      {dernierCree && (
        <div role="status" className="flex items-start gap-2 rounded-lg border border-sapin-200 bg-sapin-50 p-3 text-sm text-sapin-900">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span className="flex-1">
            Dossier {dernierCree.ticketRef ?? ''} créé, à qualifier. Aucun message n’est parti vers l’expéditeur.{' '}
            {onOuvrirDossier && (
              <button type="button" onClick={() => onOuvrirDossier(dernierCree.ticketId)} className={`${LIEN} ${TACTILE}`}>
                Ouvrir le dossier {dernierCree.ticketRef ?? ''}
              </button>
            )}
          </span>
          <button type="button" onClick={() => setDernierCree(null)} aria-label="Masquer" className={`opacity-70 hover:opacity-100 ${TACTILE}`}>
            <XCircle className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {erreur && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <XCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>Liste indisponible : {erreur}</span>
        </div>
      )}

      {chargement && !donnees ? (
        <div className="flex items-center justify-center py-16 text-graphite">
          <Loader2 className="w-5 h-5 animate-spin mr-2" aria-hidden="true" /> Chargement du courrier écarté…
        </div>
      ) : !erreur && elements.length === 0 ? (
        <Card className="py-14 text-center">
          <Inbox className="w-8 h-8 mx-auto text-slate-300" aria-hidden="true" />
          <p className="mt-3 font-medium text-encre">Aucun e-mail écarté</p>
          <p className="text-sm text-graphite mt-1">
            {motif ? 'Aucun e-mail pour ce motif.' : 'Tous les e-mails reçus ont ouvert un dossier ou rejoint un dossier existant.'}
          </p>
        </Card>
      ) : elements.length > 0 && (
        <Card className="overflow-hidden">
          {etroit ? (
            <ul aria-label="Courrier écarté" className="divide-y divide-trait">
              {elements.map(c => (
                <li key={c.id} className="p-4 space-y-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-encre break-words">{c.subject || '(sans objet)'}</p>
                    <p className="text-xs text-graphite break-all">{expediteurLisible(c)}</p>
                    <p className="text-xs text-graphite tnum">
                      Reçu le {dateLisible(c.receivedAt)}{c.boite ? ` · ${c.boite}` : ''}
                    </p>
                  </div>
                  <DetailMotif c={c} />
                  <ActionCourrier c={c} cree={crees[c.id]} onCreer={demanderCreation} onOuvrirDossier={onOuvrirDossier} pleineLargeur />
                </li>
              ))}
            </ul>
          ) : (
            <table className="w-full">
              <thead className="bg-white border-b border-trait">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-graphite whitespace-nowrap">Reçu le</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-graphite">Expéditeur</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-graphite">Objet</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-graphite">Motif</th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-graphite"><span className="sr-only">Action</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-trait">
                {elements.map(c => (
                  <tr key={c.id} className="align-top hover:bg-brume/60">
                    <td className="px-4 py-3 text-sm text-encre tnum whitespace-nowrap">{dateLisible(c.receivedAt)}</td>
                    <td className="px-4 py-3 text-sm max-w-[16rem]">
                      <p className="text-encre break-words">{c.fromName || c.fromAddress}</p>
                      {c.fromName && <p className="text-xs text-graphite break-all">{c.fromAddress}</p>}
                      {c.boite && <p className="text-xs text-graphite">Boîte : {c.boite}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-encre max-w-[18rem] break-words">{c.subject || '(sans objet)'}</td>
                    <td className="px-4 py-3 max-w-[24rem]"><DetailMotif c={c} /></td>
                    <td className="px-4 py-3 text-right">
                      <ActionCourrier c={c} cree={crees[c.id]} onCreer={demanderCreation} onOuvrirDossier={onOuvrirDossier} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-trait text-sm text-graphite">
            <span className="tnum">{total} e-mail{total > 1 ? 's' : ''} écarté{total > 1 ? 's' : ''}</span>
            {pages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1 || chargement} onClick={() => setPage(p => Math.max(1, p - 1))} className={TACTILE}>Précédent</Button>
                <span className="tnum">Page {page} sur {pages}</span>
                <Button variant="secondary" size="sm" disabled={page >= pages || chargement} onClick={() => setPage(p => p + 1)} className={TACTILE}>Suivant</Button>
              </div>
            )}
          </div>
        </Card>
      )}

      <Modal
        isOpen={aConfirmer !== null}
        onClose={() => { if (!creation) setAConfirmer(null); }}
        title="Créer un dossier quand même ?"
        size="sm"
      >
        {aConfirmer && (
          <div className="space-y-4">
            <p className="text-sm text-graphite">{texteConfirmationCreation(aConfirmer)}</p>
            <p className="text-xs text-graphite flex items-start gap-1.5">
              <MailX className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              Motif de l’écartement : {motifLisible(aConfirmer)}{confianceLisible(aConfirmer.confiance) ? ` (${confianceLisible(aConfirmer.confiance)})` : ''}.
            </p>
            {erreurCreation && (
              <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{erreurCreation}</p>
            )}
            <div className={ACTIONS_FENETRE}>
              <Button variant="secondary" disabled={creation} onClick={() => setAConfirmer(null)} className={BOUTON_FENETRE}>Annuler</Button>
              <Button variant="primary" disabled={creation} onClick={() => void confirmerCreation()} className={BOUTON_FENETRE}>
                {creation && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" />}
                Créer le dossier
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CourrierEcarteView;
